import {useTranslationWithKeys} from '../../../hooks/useTranslationWithKeys';
import {useNavigate} from 'react-router-dom';
import {Button} from '../../basic/button';
import {Card, CardContent, CardHeader, CardTitle} from '../../basic/card';
import {AlertTriangle, Download, RefreshCw, X} from 'lucide-react';

interface ErrorFallbackProps {
    error: Error;
    onReset?: () => void;
}

export default function ErrorFallback({error, onReset}: ErrorFallbackProps) {
    const {t} = useTranslationWithKeys();
    const navigate = useNavigate();
    const errorReference = `ERR-${Date.now()}`;
    const showStackTraces = import.meta.env.DEV;

    const handleReload = () => {
        window.location.reload();
    };

    const handleContactSupport = () => {
        navigate('/support');
    };

    const handleDownloadReport = () => {
        const timestamp = new Date().toISOString();
        const userAgent = navigator.userAgent;
        const currentUrl = window.location.href;

        const reportContent = [
            `Error Report - ${timestamp}`,
            `Reference ID: ${errorReference}`,
            '',
            'Error Information:',
            `Message: ${error.message}`,
            `Name: ${error.name}`,
            '',
            'Error Stack:',
            error.stack || 'No stack trace available',
            '',
            'Environment Information:',
            `Timestamp: ${timestamp}`,
            `User Agent: ${userAgent}`,
            `Current URL: ${currentUrl}`,
        ].join('\n');

        const blob = new Blob([reportContent], {type: 'text/plain'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `error-report-${errorReference}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <Card className="w-full max-w-2xl">
                <CardHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-destructive/10 rounded-full">
                            <AlertTriangle className="h-6 w-6 text-destructive"/>
                        </div>
                        <CardTitle className="text-xl">{t('error.boundary.root.title')}</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-muted-foreground">{t('error.boundary.root.message')}</p>

                    <div className="bg-muted/50 p-4 rounded-lg">
                        <p className="text-sm font-medium">{error.message}</p>
                    </div>

                    <div className="text-sm text-muted-foreground">
                        <p>{t('error.boundary.reference')}: {errorReference}</p>
                    </div>

                    {showStackTraces && (
                        <div className="bg-destructive/5 p-4 rounded-lg overflow-auto max-h-48">
                            <h4 className="text-sm font-semibold mb-2">Error Stack:</h4>
                            <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                                {error.stack || 'No stack trace available'}
                            </pre>
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <Button onClick={handleReload} className="flex-1">
                            <RefreshCw className="h-4 w-4 mr-2"/>
                            {t('error.boundary.root.reload')}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleContactSupport}
                            className="flex-1"
                        >
                            {t('error.boundary.root.contact')}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleDownloadReport}
                            className="flex-1"
                        >
                            <Download className="h-4 w-4 mr-2"/>
                            {t('error.boundary.root.downloadReport')}
                        </Button>
                        {onReset && (
                            <Button
                                variant="ghost"
                                onClick={onReset}
                                className="flex-1"
                            >
                                <X className="h-4 w-4 mr-2"/>
                                {t('error.boundary.dismiss')}
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
